import React from 'react';
import { Link } from 'react-router-dom';
import { getIcon } from './NavbarUtils';
import { ChevronDown, ChevronRight } from 'lucide-react';

interface DropdownLayoutsProps {
  navId: string;
  dropdownData: any[];
}

export const DropdownLayouts: React.FC<DropdownLayoutsProps> = ({ navId, dropdownData }) => {
  // Special layout for quick-links with better grouping
  const renderQuickLinksLayout = () => {
    if (navId !== 'quick-links') return null;
    
  const allItems = dropdownData.flatMap(section => section.items);
    
    // Group items by category
    const groupedItems = {
      'Tools & Apps': allItems.filter(item => 
        ['advance-tool', 'ar-tool', 'alternative-part-code', 'dashboard'].includes(item.id)
      ),
      'HR & Expenses': allItems.filter(item => 
        ['employee-expense-bgd', 'employee-expense-ind', 'employee-expense-lka', 'employee-discounts', 'hrms', 'sap-successfactors'].includes(item.id)
      ),
      'IT & Support': allItems.filter(item => 
        ['it-helpdesk', 'web-email', 'workflow-automation'].includes(item.id)
      ),
      'Business Operations': allItems.filter(item => 
        ['invoice-cancellation', 'jv-entry', 'legatrix', 'sales-portal', 'srn-pod', 'ff-workflow'].includes(item.id)
      )
    };
    
    return (
      <div className="grid grid-cols-4 gap-6">
        {Object.entries(groupedItems).map(([groupName, items]) => (
          <div key={groupName} className="space-y-2">
            <h3 className="text-xs font-bold text-gray-800 uppercase tracking-wider mb-3 pb-1 border-b border-gray-200">
              {groupName}
            </h3>
            <div className="space-y-1">
              {items.map((item) => (
                <Link
                target='_blank'
                hrefLang='en'
                  key={item.id}
                  to={item.to}
                  className="flex items-center space-x-2 px-3 py-2 rounded-md text-sm transition-all duration-200 hover:bg-gray-50 hover:translate-x-1 group text-gray-700 hover:text-gray-900 font-medium"
                >
                  <span className="text-gray-500 group-hover:text-current">
                    {getIcon(item.id, item.label)}
                  </span>
                  <span>{item.label}</span>
                </Link>
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  };

  // Special layout for About Us - removed state passing for countries


  const renderAboutLayout = () => {
    if (navId !== "about") return null;
    const reorderedSections = [dropdownData[0], dropdownData[2], dropdownData[1]];
    
    return (
      <div className="grid grid-cols-3 gap-8">
        {reorderedSections.map((section, sectionIndex) => (
          <div key={sectionIndex} className="space-y-2">
            {section.title && (
              <h3 className="text-xs font-bold text-gray-800 uppercase tracking-wider mb-3 pb-1 border-b border-gray-200">
                {section.title}
              </h3>
            )}
            <div className="space-y-1">
              {section.items.map((item: any) => {
                const hasChildren = item.children && item.children.length > 0;
                const isExternal = Boolean(item.href);
                return (
                  <div key={item.id} className="relative group/item">
                    {/* Parent link */}
                    {isExternal?<a
                    target='_blank'
                      href={item?.href}
                      className="flex items-center justify-between px-3 py-2 rounded-md text-sm transition-all duration-200 hover:bg-gray-50 text-gray-700 hover:text-gray-900 font-medium"
                    >
                      <div className="flex items-center space-x-2">
                        <span className="text-gray-500 group-hover/item:text-current">
                          {getIcon(item.id, item.label)}
                        </span>
                        <span>{item.label}</span>
                      </div>
                      {hasChildren && (
                        <ChevronRight className="w-4 h-4 text-gray-400 group-hover/item:text-gray-600" />
                      )}
                    </a>:<Link
                    
                      to={item.to || "#"}
                      className="flex items-center justify-between px-3 py-2 rounded-md text-sm transition-all duration-200 hover:bg-gray-50 text-gray-700 hover:text-gray-900 font-medium"
                    >
                      <div className="flex items-center space-x-2">
                        <span className="text-gray-500 group-hover/item:text-current">
                          {getIcon(item.id, item.label)}
                        </span>
                        <span>{item.label}</span>
                      </div>
                      {hasChildren && (
                        <ChevronRight className="w-4 h-4 text-gray-400 group-hover/item:text-gray-600" />
                      )}
                    </Link>}
                    
                    {/* Flyout submenu with hover bridge */}
                    {hasChildren && (
                      <div className="absolute left-full top-0 hidden group-hover/item:block z-50">
                        {/* Invisible hover bridge to maintain hover state */}
                        <div className="w-2 h-full absolute right-full top-0"></div>
                        
                        <div className="w-48 bg-white border border-gray-200 rounded-lg shadow-lg ml-2">
                          <ul className="py-2">
                            {item.children.map((child: any) => (
                              <li key={child.id}>
                                <Link
                                  to={child.to}
                                  className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-gray-900"
                                >
                                  {child.label}
                                </Link>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    );
  };

  // Default layout for simple dropdowns
  const renderDefaultLayout = () => {
    if (['about', 'quick-links'].includes(navId)) return null;
    
    return (
      <div className="space-y-2">
        {dropdownData.map((section, sectionIndex) => (
          <div key={sectionIndex}>
            {section.title && (
              <h3 className="text-xs font-bold text-gray-800 uppercase tracking-wider mb-3 pb-1 border-b border-gray-200">
                {section.title}
              </h3>
            )}
            <div className="space-y-1">
              {section.items.map((item : any) => (
                item.isExternal?<a
                    target='_blank'
                      href={item?.href}
                      className="flex items-center justify-between px-3 py-2 rounded-md text-sm transition-all duration-200 hover:bg-gray-50 text-gray-700 hover:text-gray-900 font-medium"
                    >
                      <div className="flex items-center space-x-2">
                        <span className="text-gray-500 group-hover/item:text-current">
                          {getIcon(item.id, item.label)}
                        </span>
                        <span>{item.label}</span>
                      </div>
                     
                    </a>:<Link
                  key={item.id}
                  to={item.to}
                  
                  className="flex items-center space-x-2 px-3 py-2 rounded-md text-sm transition-all duration-200 hover:bg-gray-50 hover:translate-x-1 group text-gray-700 hover:text-gray-900 font-medium"
                >
                  <span className="text-gray-500 group-hover:text-current">
                    {getIcon(item.id, item.label)}
                  </span>
                  <span>{item.label}</span>
                </Link>
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  };

  // Render the appropriate layout based on navId
  return (
    <>
      {navId === 'about' && renderAboutLayout()}
      {navId === 'quick-links' && renderQuickLinksLayout()}
      {!['about', 'quick-links'].includes(navId) && renderDefaultLayout()}
    </>
  );
};